import React, { useState, useEffect } from 'react';
import { Phone } from 'lucide-react';

interface TelefonoInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
}

export const TelefonoInput: React.FC<TelefonoInputProps> = ({
  value,
  onChange,
  placeholder = "Número de teléfono",
  className = '',
  error,
  disabled = false,
  required = false
}) => {
  const [displayValue, setDisplayValue] = useState('');

  // Formatear número de teléfono argentino
  const formatearTelefono = (input: string): string => {
    // Remover todos los caracteres no numéricos excepto el +
    let cleaned = input.replace(/[^\d+]/g, '');
    
    // Si no empieza con +54, agregarlo automáticamente si tiene números
    if (cleaned && !cleaned.startsWith('+54')) {
      // Si empieza con 54, agregar el +
      if (cleaned.startsWith('54')) {
        cleaned = '+' + cleaned;
      }
      // Si empieza con 9, es un celular sin código de país
      else if (cleaned.startsWith('9')) {
        cleaned = '+549' + cleaned.substring(1);
      }
      // Si no tiene código de país, agregarlo
      else if (cleaned.length >= 8) {
        cleaned = '+549' + cleaned;
      }
    }
    
    // Formatear para mostrar: +54 9 11 1234-5678
    if (cleaned.startsWith('+549')) {
      const numbers = cleaned.substring(4); // Remover +549
      if (numbers.length >= 10) {
        const areaCode = numbers.substring(0, 2);
        const firstPart = numbers.substring(2, 6);
        const secondPart = numbers.substring(6, 10);
        return `+54 9 ${areaCode} ${firstPart}-${secondPart}`;
      } else if (numbers.length >= 6) {
        const areaCode = numbers.substring(0, 2);
        const firstPart = numbers.substring(2, 6);
        const secondPart = numbers.substring(6);
        return `+54 9 ${areaCode} ${firstPart}${secondPart ? '-' + secondPart : ''}`;
      } else if (numbers.length >= 2) {
        const areaCode = numbers.substring(0, 2);
        const rest = numbers.substring(2);
        return `+54 9 ${areaCode}${rest ? ' ' + rest : ''}`;
      } else {
        return `+54 9${numbers ? ' ' + numbers : ''}`;
      }
    }
    
    return cleaned;
  };

  // Obtener valor limpio para enviar al backend (solo números con +54)
  const obtenerValorLimpio = (formatted: string): string => {
    const cleaned = formatted.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+54')) {
      return cleaned;
    }
    return formatted; // Devolver tal como está si no está formateado
  };

  useEffect(() => {
    if (value) {
      setDisplayValue(formatearTelefono(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formatted = formatearTelefono(inputValue);
    const cleanValue = obtenerValorLimpio(formatted);
    
    setDisplayValue(formatted);
    onChange(cleanValue);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Si está vacío, pre-llenar con +54 9 
    if (!displayValue) {
      const prefijo = '+54 9 ';
      setDisplayValue(prefijo);
      onChange('+549');
      // Posicionar cursor al final
      setTimeout(() => {
        e.target.setSelectionRange(prefijo.length, prefijo.length);
      }, 0);
    }
  };

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Phone className="h-4 w-4 text-gray-400" />
      </div>
      
      <input
        type="tel"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={`
          pl-10 w-full px-3 py-2 text-sm sm:text-base border rounded-lg 
          focus:ring-2 focus:ring-blue-500 focus:border-transparent 
          dark:bg-gray-700 dark:border-gray-600 dark:text-white
          ${error 
            ? 'border-red-300 dark:border-red-600' 
            : 'border-gray-300 dark:border-gray-600'
          }
          ${disabled 
            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' 
            : 'bg-white dark:bg-gray-700'
          }
          ${className}
        `}
      />
      
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      
      {/* Ayuda visual */}
      {displayValue && displayValue.startsWith('+54') && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          ✓ Formato argentino detectado
        </p>
      )}
    </div>
  );
};
